import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getSalesReport } from '../../api/distributionService';
import { SalesReport } from '../../models/distribution';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface SalesDashboardProps {
  timeRange?: 'day' | 'week' | 'month' | 'year';
}

export default function   SalesDashboard({ timeRange = 'week' }: SalesDashboardProps) {
  const { locationId } = useParams<{ locationId: string }>();
  const [report, setReport] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const now = new Date();
        let startDate = new Date();

        switch (timeRange) {
          case 'day':
            startDate.setDate(now.getDate() - 1);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }

        const data = await getSalesReport(locationId!, startDate, now);
        setReport(data);
      } catch (err) {
        setError('Failed to load sales data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (locationId) loadData();
  }, [locationId, timeRange]);

  if (loading) return <div className="p-4">Loading sales data...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!report) return <div className="p-4">No sales data available</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard 
          title="Total Sales" 
          value={report.total} 
          format={formatCurrency}
          icon="💰"
        />
        <KPICard 
          title="Total Profit" 
          value={report.profit} 
          format={formatCurrency}
          icon="📈"
        />
        <KPICard 
          title="Transactions" 
          value={report.count} 
          icon="🧾"
        />
      </div>

      {/* Paument Channels */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Payment Channels</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Payment Channel</TableHeader>
                <TableHeader>Txn</TableHeader>
                <TableHeader>Amount</TableHeader>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.paymentChannels.map((paymentChannel, index) => (
                <PaymentChannelTableRow key={index} paymentChannel={paymentChannel} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sales List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Recent Sales</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Date</TableHeader>
                <TableHeader>Customer</TableHeader>
                <TableHeader>Quantity (kg)</TableHeader>
                <TableHeader>Sale Price</TableHeader>
                <TableHeader>Purchase Price</TableHeader>
                <TableHeader>Profit</TableHeader>
                <TableHeader>Total</TableHeader>
                <TableHeader>Channel</TableHeader>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.sales.map((sale) => (
                <TableRow key={sale.id} sale={sale} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Sub-components for better organization
interface KPICardProps {
  title: string;
  value: number;
  format?: (value: number) => string;
  icon: string;
}

function KPICard({ title, value, format = (v) => v.toString(), icon }: KPICardProps) {
  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold">
        {format(value)}
      </p>
    </div>
  );
}

function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

function TableRow({ sale }: { sale: SalesReport['sales'][0] }) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(sale.date)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {sale.customerId ? `Customer ${sale.customerId.substring(0, 6)}...` : 'Walk-in'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {sale.quantityKg.toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatCurrency(sale.pricePerKg)}/kg
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {sale.purchasePrice ? formatCurrency(sale.purchasePrice) : 'N/A'}
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
        sale.profit >= 0 ? 'text-green-600' : 'text-red-600'
      }`}>
        {formatCurrency(sale.profit)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {formatCurrency(sale.totalAmount)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {sale.paymentChannel}
      </td>
    </tr>
  );
}

function PaymentChannelTableRow({ paymentChannel }: { paymentChannel: SalesReport['paymentChannels'][0] }) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {paymentChannel.channel}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {paymentChannel.count.toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatCurrency(paymentChannel.totalAmount)}
      </td>
    </tr>
  );
}